import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Users, Inbox, Shield, Globe, Wrench, Lock, CheckCircle2, ArrowUpRight } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, icon: Icon, hue = 'red' }) => (
  <div className="bg-white p-6 border border-neutral-200">
    <div className={`w-11 h-11 flex items-center justify-center mb-4 ${hue === 'red' ? 'bg-[#c8102e]' : 'bg-black'}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div className="font-serif-display text-4xl font-bold text-black">{value}</div>
    <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mt-1">{label}</div>
  </div>
);

const STATUS_META = {
  public: { icon: Globe, label: 'Public', color: 'text-emerald-600', desc: 'Site is live and visible to everyone.' },
  private: { icon: Lock, label: 'Private', color: 'text-amber-600', desc: 'Only signed-in users can view the site.' },
  maintenance: { icon: Wrench, label: 'Maintenance', color: 'text-[#c8102e]', desc: 'Public visitors see a maintenance page.' },
};

const AdminDashboard = () => {
  const { token } = useAuth();
  const { settings } = useSiteSettings();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStats(r.data))
      .catch(() => setStats({ users: 0, admins: 0, submissions: 0, unread_submissions: 0 }));
  }, [token]);

  const status = STATUS_META[settings?.site_status || 'public'];
  const S = status.icon;

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Overview</span></div>
        <h1 className="font-serif-display text-4xl font-bold text-black">Dashboard</h1>
      </div>

      <div className="bg-white p-6 border border-neutral-200 mb-6 flex items-center gap-5">
        <div className="w-14 h-14 bg-neutral-50 flex items-center justify-center"><S className={`w-6 h-6 ${status.color}`} /></div>
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold mb-1">Current Site Status</div>
          <div className={`font-serif-display text-2xl font-bold ${status.color}`}>{status.label}</div>
          <div className="text-sm text-neutral-600 mt-1">{status.desc}</div>
        </div>
        <Link to="/admin/site" className="inline-flex items-center gap-2 border border-black hover:bg-black hover:text-white h-11 px-5 text-[12px] tracking-[0.15em] uppercase font-semibold transition-colors">
          Change <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total Users" value={stats?.users ?? '—'} icon={Users} />
        <StatCard label="Admins" value={stats?.admins ?? '—'} icon={Shield} hue="black" />
        <StatCard label="Submissions" value={stats?.submissions ?? '—'} icon={Inbox} hue="black" />
        <StatCard label="Unread" value={stats?.unread_submissions ?? '—'} icon={CheckCircle2} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/admin/site" className="group bg-black text-white p-6 hover:bg-[#c8102e] transition-colors">
          <Globe className="w-6 h-6 text-[#c8102e] group-hover:text-white mb-4 transition-colors" />
          <h3 className="font-serif-display text-xl font-bold mb-1">Customize Site</h3>
          <p className="text-sm text-white/70">Hero copy, contact info, section toggles, announcement bar.</p>
        </Link>
        <Link to="/admin/submissions" className="group bg-white p-6 border border-neutral-200 hover:border-black transition-colors">
          <Inbox className="w-6 h-6 text-[#c8102e] mb-4" />
          <h3 className="font-serif-display text-xl font-bold text-black mb-1">Contact Inbox</h3>
          <p className="text-sm text-neutral-600">Review messages sent from the Contact page.</p>
        </Link>
        <Link to="/admin/users" className="group bg-white p-6 border border-neutral-200 hover:border-black transition-colors">
          <Users className="w-6 h-6 text-[#c8102e] mb-4" />
          <h3 className="font-serif-display text-xl font-bold text-black mb-1">Users</h3>
          <p className="text-sm text-neutral-600">Promote admins, remove accounts.</p>
        </Link>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
